<template>
  <l-rows>
    <a-title
      :text="$t('reporting.gamePerformanceReports')"
    />
    <!-- Filter -->
    <l-columns>
      <casino-select
        v-model="casinoId"
        :label="$t('common.casino')"
      />
      <game-select
        v-model="gameId"
        nullable
        fullwidth
      />
      <currency-select
        v-model="currency"
      />
      <m-input-date
        v-model="from"
        :label="$t('common.start')"
      />
      <m-input-date
        v-model="to"
        :label="$t('common.end')"
      />
    </l-columns>
    <a-box>
      <div class="table-wrapper">
        <table class="table">
          <thead>
            <tr>
              <th
                v-for="column in columns"
                :key="column.field"
                :class="column.contentPosition"
              >
                {{ column.header }}
              </th>
            </tr>
          </thead>
          <tbody v-if="!pageMeta.loading">
            <tr
              v-for="item in page.gamePerformanceReports"
              :key="item.id"
            >
              <td
                v-for="column in columns"
                :key="column.field"
                :class="column.contentPosition"
              >
                {{ column.filter ? column.filter(item[column.field], item) : item[column.field] }}
              </td>
            </tr>
            <tr
              v-for="(sums, curr) in columnSums()"
              :key="curr"
            >
              <td
                v-for="(column, index) in sums"
                :key="index"
                class="sum-col"
              >
                {{ column }}
              </td>
              <td class="sum-curr">
                {{ curr }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <l-rows>
        <a-loading
          v-if="pageMeta.loading"
          loading
        />
        <l-columns
          v-else-if="noEntries"
          centered-x
        >
          <l-columns-column size="narrow">
            {{ $t('common.noResults') }}
          </l-columns-column>
        </l-columns>
      </l-rows>
      <div class="button-box">
        <a-button
          asynch
          type="button"
          variant="primary"
          :text="this.$t('reporting.downloadReport')"
          @click="downloadGamePerformance"
        />
      </div>
    </a-box>
  </l-rows>
</template>

<script>
  import { AButton, ABox, ALoading, ATitle, LColumns, LColumnsColumn, LRows, MInputDate } from 'codefabrik-gui-vue'
  import { CasinoSelect, GameSelect, CurrencySelect } from '@/components'
  import { reportingService } from '@/services'
  import useQuery from 'codefabrik-gui-vue/src/apollo/mixins/useQuery'
  import {formatDateTime, formatMoney, formatPercent, toast} from '@/shared'

  export default {
    name: 'GamePerformanceReportList',
    components: { AButton, GameSelect, CasinoSelect, CurrencySelect, LRows, ATitle, MInputDate, LColumns, LColumnsColumn, ABox, ALoading },
    data () {
      return {
        casinoId: null,
        gameId: null,
        from: null,
        to: null,
        currency: null,
        limit: 0,
        offset: 0
      }
    },
    computed: {
      columns () {
        return [
          {
            header: this.$t('common.gameId'),
            field: 'gameId',
          },
          {
            header: this.$t('common.rtp'),
            field: 'rtp',
            contentPosition: 'right',
            filter: formatPercent
          },
          {
            header: this.$t('common.theoreticalRtp'),
            field: 'theoreticalRtp',
            contentPosition: 'right',
            filter: formatPercent
          },
          {
            header: this.$t('common.gameCycles'),
            field: 'gameCycles',
            contentPosition: 'right'
          },
          {
            header: this.$t('common.totalWagered'),
            field: 'totalWagered',
            contentPosition: 'right',
            filter: (value, item) => formatMoney(value, item.currency)
          },
          {
            header: this.$t('common.totalWon'),
            field: 'totalWon',
            contentPosition: 'right',
            filter: (value, item) => formatMoney(value, item.currency)
          },
          {
            header: this.$t('common.wageredInGamble'),
            field: 'wageredInGamble',
            contentPosition: 'right',
            filter: (value, item) => formatMoney(value, item.currency)
          },
          {
            header: this.$t('common.wonInGamble'),
            field: 'wonInGamble',
            contentPosition: 'right',
            filter: (value, item) => formatMoney(value, item.currency)
          },
          {
            header: this.$t('common.gameCyclesGambled'),
            field: 'gameCyclesGambled',
            contentPosition: 'right'
          },
          {
            header: this.$t('common.gameCyclesGambledAndWon'),
            field: 'gameCyclesGambledAndWon',
            contentPosition: 'right'
          },
          {
            header: this.$t('common.wageredInOpen'),
            field: 'wageredInOpen',
            contentPosition: 'right',
            filter: (value, item) => formatMoney(value, item.currency)
          },
          {
            header: this.$t('common.wageredInRefunded'),
            field: 'wageredInRefunded',
            contentPosition: 'right',
            filter: (value, item) => formatMoney(value, item.currency)
          },
          {
            header: this.$t('common.firstPlay'),
            field: 'firstPlay',
            filter: formatDateTime
          },
          {
            header: this.$t('common.currency'),
            field: 'currency',
          },
        ]
      },
      noEntries () {
        return this.page.gamePerformanceReports != null && this.page.gamePerformanceReports.length === 0
      }
    },
    methods: {
      async displayError(response) {
        toast.showErrorToast(this.$t(await response.text()))
      },
      handlePageChange (pageNumber) {
        this.offset = (pageNumber - 1) * this.limit
      },
      downloadGamePerformance () {
        let data = {casinoId: this.casinoId, gameId: this.gameId, from: this.from, to: this.to, currency: this.currency, offset: this.offset, limit: this.limit};
        reportingService
          .exportGamePerformanceToExcel(data)
          .catch(this.displayError)
      },
      columnSums(){
        let sumByCurrency = {};
        if(this.page.gamePerformanceReports) {
          this.page.gamePerformanceReports.forEach(function (value) {
            let currency = value.currency
            if(sumByCurrency[currency] === undefined){
              sumByCurrency[currency] = new Array(13); for (let i=3; i<12; ++i) sumByCurrency[currency][i] = 0;
            }
            let index = 3;
            let sums = sumByCurrency[currency];
            sums[index++] += value.gameCycles;
            sums[index++] += parseInt(value.totalWagered);
            sums[index++] += parseInt(value.totalWon);

            sums[index++] += parseInt(value.wageredInGamble);
            sums[index++] += parseInt(value.wonInGamble);
            sums[index++] += parseInt(value.gameCyclesGambled);

            sums[index++] += parseInt(value.gameCyclesGambledAndWon);
            sums[index++] += parseInt(value.wageredInOpen);
          });

          for(let currency in sumByCurrency) {
            let index = 4;
            sumByCurrency[currency][index] = formatMoney(sumByCurrency[currency][index++], currency);
            sumByCurrency[currency][index] = formatMoney(sumByCurrency[currency][index++], currency);
            sumByCurrency[currency][index] = formatMoney(sumByCurrency[currency][index++], currency);
            sumByCurrency[currency][index] = formatMoney(sumByCurrency[currency][index++], currency);
            sumByCurrency[currency][index] = sumByCurrency[currency][index++];
            sumByCurrency[currency][index] = sumByCurrency[currency][index++];
            sumByCurrency[currency][index] = formatMoney(sumByCurrency[currency][index++], currency);
            sumByCurrency[currency][index] = formatMoney(sumByCurrency[currency][index++], currency);
          }
        }
        return sumByCurrency;
      },
    },
    mixins: [
      useQuery({
        name: 'page',
        serviceFn: reportingService.getGamePerformanceRevenueReportPage,
        skipIf () {
          return this.casinoId == null
        },
        variables () {
          return {
            input: {
              casinoId: this.casinoId,
              gameId: this.gameId,
              from: this.from,
              to: this.to,
              currency: this.currency,
              limit: this.limit,
              offset: this.offset
            }
          }
        }
      })
    ]
  }
</script>

<style lang="scss" scoped>
.table-wrapper {
  overflow: scroll;

  th, td {
    white-space: nowrap;
  }

  th.right, td.right {
    text-align: right;
  }
}
.button-box{
  margin-top: 10px;
  text-align: right
}
.sum-col{
  text-align: right;
  font-weight: bold;
}
.sum-curr{
  font-weight: bold;
}
.button-box{
  margin-top: 10px;
  text-align: right
}
</style>
